/**
 * 맞춤 알림 규칙의 어휘 — 코드 값 · 화면이 고르는 선택지 목록 · Firestore 컬렉션 이름.
 *
 * 모양은 `types/alert-rule/alert-rule-domain` · 계산은 `utils/alert-rule/alert-rule-utils`.
 * 저장소 접근이 없어(SDK 없음) 서버 route 와 브라우저가 같은 값을 여기서 읽는다.
 */

import { SET } from 'src/constants/track-set-name'
import { AI_FEATURES_OPEN } from 'src/constants/ai-features'

export const ALERT_SOURCE_TYPES = {
  BRAND_MENTION: 'brand_mention',
  KEYWORD_MONITORING: 'keyword_monitoring',
  SEEDING_CAMPAIGN: 'seeding_campaign',
}

/** 소스별 Firestore 캠페인 컬렉션 (대시보드·keyword-view·campaign-view와 동일) */
export const ALERT_CAMPAIGN_COLLECTIONS = {
  KEYWORD_MONITORING: 'CatchMomentsCampaigns',
  SEEDING_CAMPAIGN: 'CatchMomentsInfluencerCampaign',
}

export const ALERT_CONDITION_TYPES = {
  METRIC_THRESHOLD: 'metric_threshold',
  VIEW_COUNT: 'view_count',
  ENGAGEMENT_RATE: 'engagement_rate',
  /** @deprecated 빈 conditions[] 와 동일 — 레거시 문서 호환용 */
  ALL_NEW_POSTS: 'all_new_posts',
}

export const ALERT_SCHEDULE_MODES = {
  REALTIME: 'realtime',
  HOURLY: 'hourly',
  DAILY: 'daily',
  WEEKLY: 'weekly',
}

/** 알림 본문에 포함할 항목 */
export const ALERT_CONTENT_TYPES = {
  POSTING_LIST: 'posting_list',
  STATISTICS: 'statistics',
  AI_SUMMARY: 'ai_summary',
  MENTIONED_BRANDS: 'mentioned_brands',
  NEGATIVE_MENTIONS: 'negative_mentions',
}

/**
 * 알림 본문에 넣을 수 있는 항목 — 화면의 칩 · 저장 정규화(`normalizeAlertContent`) · 카드 라벨이 이 목록을 읽는다.
 * 🚨 앱 안의 AI 스위치(`constants/ai-features`)가 꺼져 있으면(지금) 「AI 요약」이 목록에서 빠진다 — 고를 수 없고,
 *   정규화가 그 칸을 `false` 로 채우므로 저장하면 꺼진다(서버 쓰기도 같은 정규화를 탄다).
 */
export const ALERT_CONTENT_OPTIONS = [
  {
    value: ALERT_CONTENT_TYPES.POSTING_LIST,
    label: '포스팅 리스트',
    description: '조건에 맞는 포스팅 목록(제목·계정·도달·링크 등)',
  },
  {
    value: ALERT_CONTENT_TYPES.STATISTICS,
    label: '통계',
    description: '포스팅 수·총/평균 도달·채널별 요약 등 집계',
  },
  {
    value: ALERT_CONTENT_TYPES.AI_SUMMARY,
    label: 'AI 요약',
    description: '수집된 멘션 흐름과 핵심 이슈를 AI로 요약',
  },
  {
    value: ALERT_CONTENT_TYPES.MENTIONED_BRANDS,
    label: '언급 브랜드 정보',
    description: '포스팅에서 언급된 브랜드·경쟁사 순위',
  },
  {
    value: ALERT_CONTENT_TYPES.NEGATIVE_MENTIONS,
    label: '부정 멘션 알림',
    description: '부정적인 표현·키워드가 포함된 포스팅',
  },
].filter((option) => AI_FEATURES_OPEN || option.value !== ALERT_CONTENT_TYPES.AI_SUMMARY)

export const ALERT_SOURCE_OPTIONS = [
  {
    value: ALERT_SOURCE_TYPES.BRAND_MENTION,
    label: '브랜드 멘션 추적',
    description: '워크스페이스에 등록된 브랜드 키워드 멘션',
    featureKey: 'brandManagement',
  },
  {
    value: ALERT_SOURCE_TYPES.KEYWORD_MONITORING,
    label: '타겟 멘션 추적',
    description: `타겟 멘션 추적 ${SET}에서 수집·집계되는 멘션`,
    featureKey: 'keywordMonitoring',
    campaignCollection: ALERT_CAMPAIGN_COLLECTIONS.KEYWORD_MONITORING,
  },
  {
    value: ALERT_SOURCE_TYPES.SEEDING_CAMPAIGN,
    label: '시딩 캠페인',
    description: '인플루언서 시딩 캠페인의 업로드·성과 모니터링',
    featureKey: 'influencerCampaign',
    campaignCollection: ALERT_CAMPAIGN_COLLECTIONS.SEEDING_CAMPAIGN,
  },
]

/** 추가 가능한 임계값 조건 (AND로 결합). 빈 배열 = 신규 포스팅 전체 */
export const ALERT_CONDITION_OPTIONS = [
  {
    value: ALERT_CONDITION_TYPES.METRIC_THRESHOLD,
    label: '도달 수치',
    description: '설정한 도달 수치 이상인 포스팅',
    unit: 'count',
    unitLabel: '이상',
    paramKey: 'metricMin',
    inputLabel: '최소 도달',
  },
  {
    value: ALERT_CONDITION_TYPES.VIEW_COUNT,
    label: '조회수',
    description: '설정한 조회수 이상인 포스팅',
    unit: 'count',
    unitLabel: '개 이상',
    paramKey: 'viewMin',
    inputLabel: '최소 조회수',
  },
  {
    value: ALERT_CONDITION_TYPES.ENGAGEMENT_RATE,
    label: 'E/R',
    description: '설정한 반응률(E/R) 이상인 포스팅',
    unit: 'percent',
    unitLabel: '% 이상',
    paramKey: 'erMin',
    inputLabel: '최소 E/R (%)',
  },
]

export const ALERT_SCHEDULE_OPTIONS = [
  { value: ALERT_SCHEDULE_MODES.REALTIME, label: '실시간', description: '조건 충족 즉시 (Slack 권장)' },
  { value: ALERT_SCHEDULE_MODES.HOURLY, label: '매시간', description: '1시간마다 요약' },
  { value: ALERT_SCHEDULE_MODES.DAILY, label: '매일', description: '하루 1회 요약 발송' },
  { value: ALERT_SCHEDULE_MODES.WEEKLY, label: '매주', description: '주 1회 요약 발송' },
]

export const ALERT_WEEKDAY_OPTIONS = [
  { value: 1, label: '월요일' },
  { value: 2, label: '화요일' },
  { value: 3, label: '수요일' },
  { value: 4, label: '목요일' },
  { value: 5, label: '금요일' },
  { value: 6, label: '토요일' },
  { value: 0, label: '일요일' },
]

/** 알림 규칙 필터의 「자주 쓰는 국가」 — 나머지는 더보기로 접는다 */
export const ALERT_POPULAR_COUNTRY_CODES = ['KR', 'JP', 'US', 'VN', 'IN', 'TH', 'ID', 'MX', 'EG', 'BR', 'RU'];

/** 알림 규칙 문서 */
export const ALERT_RULE_COLLECTION = 'CatchMomentsAlertRules'

/** rule 문서 하위 — 알림 발송 완료 포스트 (dedup) */
export const ALERT_SEEN_POSTS_SUBCOLLECTION = 'alertSeenPosts'

/** 규칙 저장 전에 슬랙을 먼저 연결한 경우의 임시 보관함 */
export const ALERT_SLACK_PENDING_COLLECTION = 'CatchMomentsAlertSlackPending'

/**
 * 「매월」 이메일을 화면에 세우나 — 빠른 선택 카드 · 설정 「연결」의 받는 사람 줄.
 * 「보류」 매월 이메일은 잠시 숨긴다 — 발송기·저장 칸·읽기·🔔 판정은 그대로라 켜기는 이 값 하나다.
 */
export const ALERT_MONTHLY_VISIBLE = false
